import React, { useState, useEffect } from 'react';
import { getTopAgents } from '../../api/salesApi';

const COLORS = {
    1: { bar: 'linear-gradient(180deg, #F59E0B, #D97706)', border: '#F59E0B', text: '#F59E0B' },
    2: { bar: 'linear-gradient(180deg, #94A3B8, #64748B)', border: '#94A3B8', text: '#94A3B8' },
    3: { bar: 'linear-gradient(180deg, #CD7F32, #A0522D)', border: '#CD7F32', text: '#CD7F32' },
    4: { bar: 'linear-gradient(180deg, #475569, #334155)', border: '#475569', text: '#94A3B8' },
    5: { bar: 'linear-gradient(180deg, #475569, #334155)', border: '#475569', text: '#94A3B8' },
};

const API_BASE = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// month/year can be controlled by the parent (dashboard) so the same period drives
// the revenue card; falls back to internal state when used standalone.
const TopAgentsChart = ({ month: monthProp, year: yearProp, onPeriodChange }) => {
    const now = new Date();
    const controlled = monthProp != null && yearProp != null;
    const [monthS, setMonthS] = useState(now.getMonth() + 1);
    const [yearS, setYearS] = useState(now.getFullYear());
    const month = controlled ? monthProp : monthS;
    const year = controlled ? yearProp : yearS;
    const setMonth = (m) => controlled ? onPeriodChange(m, year) : setMonthS(m);
    const setYear = (y) => controlled ? onPeriodChange(month, y) : setYearS(y);
    const [agents, setAgents] = useState([]);

    const [grown, setGrown] = useState(false);

    useEffect(() => {
        const fetch = async () => {
            try {
                const res = await getTopAgents(month, year);
                setAgents(res.data || []);
            } catch (err) { console.error(err); }
        };
        fetch();
    }, [month, year]);

    // Bars grow up from 0 each time the data changes (rank-reveal animation).
    useEffect(() => {
        setGrown(false);
        const t = setTimeout(() => setGrown(true), 60);
        return () => clearTimeout(t);
    }, [agents]);

    const maxRevenue = agents.length ? Math.max(...agents.map(a => a.totalRevenue)) : 0;
    const podiumOrder = [1, 0, 2, 3, 4].filter(i => i < agents.length);

    const getInitials = (agent) => {
        if (agent.firstName && agent.lastName) {
            return (agent.lastName[0] + agent.firstName[0]).toUpperCase();
        }
        if (agent.firstName) return agent.firstName.slice(0, 2).toUpperCase();
        if (agent.alias) return agent.alias.slice(0, 2).toUpperCase();
        return '??';
    };

    const getAgentName = (agent) => {
        const hasFirst = agent.firstName && agent.firstName.trim();
        const hasLast = agent.lastName && agent.lastName.trim();
        if (hasFirst && hasLast) return `${agent.lastName} ${agent.firstName}`;
        if (hasFirst) return agent.firstName;
        if (hasLast) return agent.lastName;
        return agent.alias || '';
    };

    const formatRM = (val) => {
        // ≥ 1M → "RM 1.12mil"; ≥ 1k → "RM 723K"; else "RM 850"
        if (val >= 1_000_000) {
            // 2 decimals, strip trailing zeros so 1,500,000 → "RM 1.5mil" not "RM 1.50mil"
            const mil = (val / 1_000_000).toFixed(2).replace(/\.?0+$/, '');
            return `RM ${mil}mil`;
        }
        if (val >= 1000) return `RM ${(val / 1000).toFixed(0)}K`;
        return `RM ${val.toFixed(0)}`;
    };

    // Generate year options: current year and 2 years back
    const yearOptions = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

    return (
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            {/* Podium reveal. The bars rise from LAST place to first, so the winner's bar
                lands last and the eye finishes on it — a chart that reveals all five at once
                has no focal point. Avatar and name follow their own bar rather than the
                clock, so each column reads as one unit.
                Everything collapses to a plain fade under prefers-reduced-motion. */}
            <style>{`
                @keyframes taFlow  { 0%,100% { background-position: 0% 0%; } 50% { background-position: 0% 100%; } }
                @keyframes taRise  { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
                @keyframes taCrown { 0% { opacity: 0; transform: translateY(-14px) scale(0.6); }
                                     60% { opacity: 1; transform: translateY(3px) scale(1.12); }
                                     80% { transform: translateY(-2px) scale(0.98); }
                                     100% { opacity: 1; transform: none; } }
                @keyframes taShine { 0% { transform: translateY(110%); } 55%,100% { transform: translateY(-110%); } }
                .ta-col   { animation: taRise 0.5s cubic-bezier(0.22,1,0.36,1) both; }
                .ta-crown { animation: taCrown 0.7s cubic-bezier(0.22,1,0.36,1) both; }
                .ta-bar   { position: relative; overflow: hidden; }
                .ta-bar-win::after {
                    content: ''; position: absolute; inset: 0;
                    background: linear-gradient(180deg, transparent, rgba(255,255,255,0.35), transparent);
                    animation: taShine 2.8s ease-in-out 1.4s infinite;
                }
                @media (prefers-reduced-motion: reduce) {
                    .ta-col, .ta-crown { animation: none !important; opacity: 1 !important; transform: none !important; }
                    .ta-bar-win::after { animation: none !important; opacity: 0 !important; }
                    .ta-bar { transition: none !important; }
                }
            `}</style>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Top 5 Agents</h3>
                <div style={{ display: 'flex', gap: 6 }}>
                    <select
                        value={month}
                        onChange={e => setMonth(Number(e.target.value))}
                        style={{ fontSize: 11, padding: '4px 6px', borderRadius: 6, border: '1px solid #e2e8f0', color: '#475569', background: '#f8fafc', cursor: 'pointer' }}
                    >
                        {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                    <select
                        value={year}
                        onChange={e => setYear(Number(e.target.value))}
                        style={{ fontSize: 11, padding: '4px 6px', borderRadius: 6, border: '1px solid #e2e8f0', color: '#475569', background: '#f8fafc', cursor: 'pointer' }}
                    >
                        {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </div>

            {!agents.length ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#94A3B8' }}>
                    No sales data for {MONTHS[month - 1]} {year}
                </div>
            ) : (
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 12, height: 250, paddingBottom: 8, paddingTop: 30 }}>
                    {podiumOrder.map(idx => {
                        const agent = agents[idx];
                        const rank = idx + 1;
                        const color = COLORS[rank];
                        const barHeight = maxRevenue > 0 ? Math.max(30, (agent.totalRevenue / maxRevenue) * 160) : 30;
                        const avatarSize = rank <= 3 ? 38 : 32;
                        const fontSize = rank <= 3 ? 12 : 10;

                        // Reveal runs last place → first, so #1 lands at the end.
                        const revealDelay = (agents.length - rank) * 0.11;

                        return (
                            <div key={agent.userId}
                                className="ta-col"
                                style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                                    animationDelay: `${revealDelay}s`,
                                }}>
                                {/* Crown drops in once the winning bar has finished rising. */}
                                {rank === 1 && (
                                    <span className="ta-crown"
                                        style={{ fontSize: 16, animationDelay: `${revealDelay + 0.85}s` }}>👑</span>
                                )}
                                {agent.profilePhotoUrl ? (
                                    <img
                                        src={`${API_BASE}${agent.profilePhotoUrl}`}
                                        alt={getAgentName(agent)}
                                        style={{ width: avatarSize, height: avatarSize, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${color.border}` }}
                                    />
                                ) : (
                                    <div style={{
                                        width: avatarSize, height: avatarSize, borderRadius: '50%',
                                        background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: 'white', fontSize: fontSize, fontWeight: 700, border: `2px solid ${color.border}`
                                    }}>
                                        {getInitials(agent)}
                                    </div>
                                )}
                                <div className={rank === 1 ? "ta-bar ta-bar-win" : "ta-bar"} style={{
                                    width: rank <= 3 ? 50 : 42, background: color.bar,
                                    backgroundSize: '100% 200%',
                                    borderRadius: '6px 6px 0 0', height: grown ? barHeight : 0,
                                    // Each bar waits its turn, then rises. The overshoot easing
                                    // gives it a little weight as it settles.
                                    transition: 'height 0.85s cubic-bezier(0.34,1.32,0.44,1)',
                                    transitionDelay: `${revealDelay}s`,
                                    animation: 'taFlow 3.5s ease-in-out infinite',
                                    boxShadow: rank === 1 ? `0 0 12px ${color.border}66` : 'none',
                                    display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 6
                                }}>
                                    <span style={{ color: 'white', fontSize: 9, fontWeight: 700, position: 'relative', zIndex: 1 }}>
                                        {formatRM(agent.totalRevenue)}
                                    </span>
                                </div>
                                <div style={{ color: color.text, fontSize: rank <= 3 ? 10 : 9, fontWeight: 600, textAlign: 'center' }}>
                                    #{rank} {getAgentName(agent)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default TopAgentsChart;
